import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {FIELD_LABEL_TRANSLATIONS} from "../../assets/translations/translations";
import axios from "axios";
import {format} from "date-fns";

function CheckoutForm() {
    const location = useLocation();
    const navigate = useNavigate();

    const {
        cartItems = [],
        couponCode,
        quote
    } = location.state || {};
    const [pickupOptions, setPickupOptions] = useState([]);
    const [pickupLoading, setPickupLoading] = useState(false);
    const [pickupError, setPickupError] = useState('');
    // ----------------------------
    // 1. MERGE UNIQUE BOOKING FIELDS
    // ----------------------------
    const [bookingFields, setBookingFields] = useState([]);
    const [participantFields, setParticipantFields] = useState([]);
    useEffect(() => {
        const fetchPickups = async () => {
            const itemsWithPickup = cartItems.filter(i => i.pickupListId);

            if (!itemsWithPickup.length) return;

            setPickupLoading(true);
            setPickupError('');

            try {
                const results = await Promise.all(
                    itemsWithPickup.map(async (item) => {
                        const res = await axios.get(
                            `${process.env.REACT_APP_API_LOCAL_URL}util/pickups`,
                            {
                                params: {
                                    pickupId: item.pickupListId
                                }
                            }
                        );

                        return {
                            productCode: item.productCode,
                            name: item.productName,
                            date: item.startTimeLocal,
                            options: res.data?.data?.pickupList?.pickupLocations || []
                        };
                    })
                );

                setPickupOptions(results);

            } catch (err) {
                console.error(err);
                setPickupError('Pickup list load failed');
            } finally {
                setPickupLoading(false);
            }
        };

        fetchPickups();
    }, [cartItems]);

    useEffect(() => {
        const bookingMap = new Map();
        const participantMap = new Map();

        cartItems.forEach(item => {
            (item.fields || []).forEach(f => {
                bookingMap.set(f.label, f);
            });

            // collect participant fields if they exist in product
            (item.participantFields || []).forEach(f => {
                participantMap.set(f.label, f);
            });
        });

        let apiBookingFields = Array.from(bookingMap.values());

// add required defaults if missing
        REQUIRED_DEFAULT_FIELDS.forEach(defaultField => {
            const exists = apiBookingFields.some(
                f => f.label === defaultField.label
            );

            if (!exists) {
                apiBookingFields.push(defaultField);
            }
        });

        setBookingFields(apiBookingFields);
        setParticipantFields(Array.from(participantMap.values()));
    }, [cartItems]);

    // ----------------------------
    // 2. FORM STATE
    // ----------------------------
    const [formData, setFormData] = useState({
        bookingFields: {},
        participants: [],
        pickup: {}
    });

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            participants: Array(
                cartItems.reduce(
                    (sum, item) => sum + (item.totalParticipants || 0),
                    0
                )
            ).fill({}).map(() => ({}))
        }));
    }, [cartItems]);

    const [errors, setErrors] = useState({});

    // ----------------------------
    // 3. UPDATE FIELD LOGIC
    // ----------------------------
    const updateField = (path, value) => {
        setFormData(prev => {
            const copy = JSON.parse(JSON.stringify(prev));

            const [root, ...rest] = path.split('.');
            if (root === 'pickup') {
                const key = rest[0];
                copy.pickup[key] = value;
            }
            if (root === 'bookingFields') {
                const key = rest.join('.');
                copy.bookingFields[key] = value;
            }

            if (root === 'participants') {
                const idx = parseInt(rest[0]);
                const key = rest.slice(1).join('.');

                if (!copy.participants[idx]) {
                    copy.participants[idx] = {};
                }

                copy.participants[idx][key] = value;
            }

            return copy;
        });
    };

    // ----------------------------
    // 4. VALIDATION
    // ----------------------------
    const validate = () => {
        const err = {};

        // ----------------------------
        // BOOKING FIELD VALIDATION
        // ----------------------------
        bookingFields.forEach(field => {
            const value = formData.bookingFields[field.label];

            if (field.requiredPerBooking) {
                if (!value || !value.trim()) {
                    err[`booking_${field.label}`] = `${field.label} は必須です`;
                }
            }

            // EMAIL VALIDATION
            if (field.label === 'Email' && value) {
                if (!isValidEmail(value)) {
                    err[`booking_${field.label}`] = `正しいメールアドレスを入力してください`;
                }
            }

            // MOBILE VALIDATION
            if ((field.label === 'Phone' || field.label === 'Mobile') && value) {
                if (!isValidMobile(value)) {
                    err[`booking_${field.label}`] =
                        `国番号付きで入力してください（例: +818123456789）`;
                }
            }
        });

        // ----------------------------
        // PARTICIPANT VALIDATION
        // ----------------------------
        formData.participants.forEach((p, idx) => {
            participantFields.forEach(field => {
                const value = p[field.label];

                if (field.requiredPerParticipant) {
                    if (!value || !value.trim()) {
                        err[`p_${idx}_${field.label}`] =
                            `参加者 ${idx + 1}：${field.label} は必須です`;
                    }
                }

                // EMAIL validation for participants
                if (field.label === 'Email' && value) {
                    if (!isValidEmail(value)) {
                        err[`p_${idx}_${field.label}`] =
                            `正しいメールアドレスを入力してください`;
                    }
                }

                // MOBILE validation for participants
                if ((field.label === 'Phone' || field.label === 'Mobile') && value) {
                    if (!isValidMobile(value)) {
                        err[`p_${idx}_${field.label}`] =
                            `国番号付きで入力してください（例: +818123456789）`;
                    }
                }
            });
        });
// ----------------------------
// PICKUP VALIDATION
// ----------------------------
        pickupOptions.forEach(item => {
            const pickupValue = formData.pickup[item.productCode];

            if (!pickupValue) {
                err[`pickup_${item.productCode}`] =
                    `${item.name} の送迎場所を選択してください`;
            }
        });
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    // ----------------------------
    // 5. NEXT STEP
    // ----------------------------
    const handleNext = () => {
        if (!validate()) return;

        navigate('/checkout', {
            state: {
                cartItems,
                formData,
                couponCode,
                quote
            }
        });
    };
    const translateLabel = (label) =>
        FIELD_LABEL_TRANSLATIONS[label] || label;
    const REQUIRED_DEFAULT_FIELDS = [
        {label: 'First Name', fieldType: 'Text', requiredPerBooking: true},
        {label: 'Last Name', fieldType: 'Text', requiredPerBooking: true},
        {label: 'Email', fieldType: 'Text', requiredPerBooking: true},
        {label: 'Mobile', fieldType: 'Text', requiredPerBooking: true}
    ];
    const FIELD_ORDER = [
        'First Name',
        'Last Name',
        'Email',
        'Phone',
        'Mobile',
        'Special Requirements'
    ];
    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const isValidMobile = (mobile) => {
        return /^\+[1-9]\d{7,14}$/.test(mobile);
    };
    const sortFields = (fields) => {
        return [...fields].sort((a, b) => {
            const indexA = FIELD_ORDER.indexOf(a.label);
            const indexB = FIELD_ORDER.indexOf(b.label);

            const rankA = indexA === -1 ? 999 : indexA;
            const rankB = indexB === -1 ? 999 : indexB;

            return rankA - rankB;
        });
    };
    const PRIMARY_FIELDS = [
        'First Name',
        'Last Name',
        'Email',
        'Phone',
        'Mobile'
    ];

    const primaryBookingFields = sortFields(
        bookingFields.filter(f =>
            PRIMARY_FIELDS.includes(f.label)
        )
    );

    const otherBookingFields = sortFields(
        bookingFields.filter(f =>
            !PRIMARY_FIELDS.includes(f.label)
        )
    );
    const renderField = (field) => (
        <>
            {field.fieldType === 'Boolean' ? (
                <select
                    className="form-select"
                    value={formData.bookingFields[field.label] ?? ''}
                    onChange={(e) =>
                        updateField(
                            `bookingFields.${field.label}`,
                            e.target.value
                        )
                    }
                >
                    <option value="">選択してください</option>
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                </select>
            ) : field.fieldType === 'List' ? (
                <select
                    className="form-select"
                    value={formData.bookingFields[field.label] || ''}
                    onChange={(e) =>
                        updateField(
                            `bookingFields.${field.label}`,
                            e.target.value
                        )
                    }
                >
                    <option value="">選択してください</option>

                    {(field.listOptions || '')
                        .split(/\r?\n/)
                        .filter(opt => opt.trim())
                        .map(opt => (
                            <option
                                key={opt}
                                value={opt}
                            >
                                {opt}
                            </option>
                        ))}
                </select>
            ) : (
                <input
                    className="form-control"
                    value={
                        formData.bookingFields[field.label] || ''
                    }
                    onChange={(e) =>
                        updateField(
                            `bookingFields.${field.label}`,
                            e.target.value
                        )
                    }
                    placeholder={translateLabel(field.label)}
                />
            )}
        </>
    );
    // ----------------------------
    // 6. UI RENDER
    // ----------------------------
    return (
        <div className="container py-5">

            <div className="text-center mb-5">
                <h2 className="fw-bold">
                    予約詳細
                </h2>

                <p className="text-muted">
                    必須情報を入力してください
                </p>
            </div>

            <div className="row g-4">

                {/* Contact Details */}
                <div className="col-12">
                    <div className="card shadow-sm border-0">

                        <div className="card-header  bg-light">
                            <h5 className="mb-0">
                                連絡先情報
                            </h5>
                        </div>

                        <div className="card-body">

                            <div className="row">

                                {primaryBookingFields.map(field => (

                                    <div
                                        key={field.label}
                                        className="col-md-6 mb-3"
                                    >

                                        <label className="form-label fw-semibold">
                                            {translateLabel(field.label)}

                                            {field.requiredPerBooking &&
                                                <span className="text-danger ms-1">*</span>
                                            }
                                        </label>

                                        {renderField(field)}

                                        {errors[`booking_${field.label}`] && (
                                            <div className="text-danger small mt-1">
                                                {errors[`booking_${field.label}`]}
                                            </div>
                                        )}

                                    </div>

                                ))}

                            </div>

                        </div>

                    </div>
                </div>


                {pickupOptions.length > 0 && <div className="col-12">

                    <div className="card shadow-sm border-0">

                        <div className="card-header bg-light">
                            <h5 className="mb-0">
                                送迎場所
                            </h5>
                        </div>
                        <div className="card-body">
                            {pickupOptions.map((item) => (


                                <div className="row">
                                    <div
                                        key={item.productCode}
                                        className="col-md-12 mb-3"
                                    >


                                        <label className="form-label fw-semibold">
                                            {`ツアーの送迎 - ${item.name}（${format(
                                                new Date(item.date),
                                                'yyyy年M月d日'
                                            )}）`}
                                        </label>

                                        <select
                                            className="form-select"
                                            value={formData.pickup[item.productCode] || ''}
                                            onChange={(e) =>
                                                updateField(
                                                    `pickup.${item.productCode}`,
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="">送迎場所を選択</option>

                                            {item.options.map((p) => (
                                                <option
                                                    key={p.id || p.locationId}
                                                    value={p.locationName}
                                                >
                                                    {p.locationName}
                                                </option>
                                            ))}
                                        </select>
                                        {errors[`pickup_${item.productCode}`] && (
                                            <div className="text-danger small mt-1">
                                                {errors[`pickup_${item.productCode}`]}
                                            </div>
                                        )}

                                    </div>
                                </div>


                            ))}
                        </div>
                    </div>
                </div>}
                {/* Other Details */}
                {otherBookingFields.length > 0 && (
                    <div className="col-12">

                        <div className="card shadow-sm border-0">

                            <div className="card-header bg-light">
                                <h5 className="mb-0">
                                    追加情報
                                </h5>
                            </div>

                            <div className="card-body">

                                <div className="row">

                                    {otherBookingFields.map(field => (

                                        <div
                                            key={field.label}
                                            className="col-md-12 mb-3"
                                        >

                                            <label className="form-label fw-semibold">
                                                {translateLabel(field.label)}

                                                {field.requiredPerBooking &&
                                                    <span className="text-danger ms-1">*</span>
                                                }
                                            </label>

                                            {renderField(field)}

                                            {errors[`booking_${field.label}`] && (
                                                <div className="text-danger small mt-1">
                                                    {errors[`booking_${field.label}`]}
                                                </div>
                                            )}

                                        </div>

                                    ))}

                                </div>

                            </div>

                        </div>

                    </div>
                )}

                {/* Participants */}
                {formData.participants.length > 0 && (
                    <div className="col-12">

                        <h4 className="mb-3">
                            参加者
                        </h4>

                        {formData.participants.map((_, idx) => (

                            <div
                                key={idx}
                                className="card shadow-sm border-0 mb-4"
                            >

                                <div className="card-header">
                                    <strong>
                                        Participant {idx + 1}
                                    </strong>
                                </div>

                                <div className="card-body">

                                    <div className="row">

                                        {sortFields(participantFields).map(field => (

                                            <div
                                                key={field.label}
                                                className="col-md-6 mb-3"
                                            >

                                                <label className="form-label fw-semibold">
                                                    {translateLabel(field.label)}

                                                    {field.requiredPerParticipant &&
                                                        <span className="text-danger ms-1">*</span>
                                                    }
                                                </label>

                                                <input
                                                    className="form-control"
                                                    value={
                                                        formData.participants[idx]?.[field.label] || ''
                                                    }
                                                    onChange={(e) =>
                                                        updateField(
                                                            `participants.${idx}.${field.label}`,
                                                            e.target.value
                                                        )
                                                    }
                                                />

                                                {errors[`p_${idx}_${field.label}`] && (
                                                    <div className="text-danger small mt-1">
                                                        {errors[`p_${idx}_${field.label}`]}
                                                    </div>
                                                )}

                                            </div>

                                        ))}

                                    </div>

                                </div>

                            </div>

                        ))}
                    </div>
                )}

                <div className="col-12 text-end">

                    <button
                        onClick={handleNext}
                        className="btn btn-primary btn-lg px-5"
                    >
                        支払いへ進む →
                    </button>

                </div>

            </div>

        </div>
    );
}

export default CheckoutForm;